import { useEffect, useState, useCallback } from "react";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { Modal, Row, Col, Pagination, Spin, Empty } from "antd";
import { deleteData, getData, updateData } from "../helpers/api";
import withRoleProtection from "../components/withRoleProtection";
import { openNotification } from "../helpers/alerts";
import { rolesConst } from "../constants/others";
import moment from "moment";
import { getLocalStorage } from "../helpers/localstorage";
import SheetOrderCard from "../components/orders/SheetOrderCard";
import ConfirmOrderForm from "../components/orders/ConfirmOrderForm";
import SheetOrderFilter from "../components/orders/SheetOrderFilter";
import { useSearchParams } from "react-router-dom";
import useQueryParams from "../hooks/useQueryParams";

const { confirm } = Modal;

const SheetOrders = () => {
    const [sheetOrders, setSheetOrders] = useState([]);
    const { updateQueryParams, queryParams } = useQueryParams()
    const [totalSheetOrders, setTotalSheetOrders] = useState(0);
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [confirmOrderModalOpen, setConfirmOrderModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);


    const user = getLocalStorage("auth")?.user;
    const isSuperAdmin = user?.role === rolesConst.SuperAdmin;
    const isConfirmationConvertion = [rolesConst.ConfirmationConvertion, rolesConst.Management].includes(user?.role);

    const showDeleteConfirm = (sheetOrderId) => {
        confirm({
            title: "Vous êtes sûr de supprimer cette commande de feuille ?",
            icon: <ExclamationCircleFilled />,
            okText: "Supprimer",
            okType: "danger",
            cancelText: "Annuler",
            onOk() {
                deleteSheetOrder(sheetOrderId);
            },
        });
    };

    const deleteSheetOrder = async (sheetOrderId) => {
        try {
            await deleteData(`/sheet-orders/${sheetOrderId}`);
            openNotification(`La commande de feuille a été supprimée avec succès`, "success");
            getSheetOrders();
        } catch (error) {
            openNotification(`Erreurs côté serveur`, "error");
        }
    };

    const handleViewOrder = (order) => {
        Modal.info({
            title: "Détails de la commande",
            content: (
                <div>
                    <p><strong>Nom:</strong> {order.name}</p>
                    <p><strong>Téléphone:</strong> {order.phone}</p>
                    <p><strong>Adresse:</strong> {order.address}</p>
                    <p><strong>Ville:</strong> {order.city}</p>
                    <p><strong>Prix:</strong> {order.price} DH</p>
                    <p><strong>Unité:</strong> {order.unit}</p>
                    <p><strong>Produit:</strong> {order.productName}</p>
                    <p><strong>Date de commande:</strong> {moment(order.orderDate).format('YYYY-MM-DD')}</p>
                    <p><strong>Statut:</strong> {order.isTreated ? "Traité" : "En attente"}</p>
                    {order.user && <p><strong>Assigné à:</strong> {order.user.name}</p>}
                </div>
            ),
            width: 600,
        });
    };

    const handleMarkTreated = async (orderId) => {
        try {
            await updateData(`/sheet-orders/${orderId}`, { isTreated: true });
            openNotification("Commande marquée comme traitée avec succès", "success");
            getSheetOrders();
        } catch (error) {
            openNotification("Erreur lors de la mise à jour", "error");
        }
    };

    const handleConfirmOrder = async (order) => {
        try {
            // Set confirmation date when the "Confirmer" button is clicked
            if (!order.confirmationDate) {
                await updateData(`/sheet-orders/${order.id}`, {
                    confirmationDate: new Date().toISOString()
                });
            }

            // Then open the confirmation form
            setSelectedOrder(order);
            setConfirmOrderModalOpen(true);
        } catch (error) {
            openNotification("Erreur lors de l'enregistrement de la date de confirmation", "error");
        }
    };

    const handleConfirmOrderModalCancel = () => {
        setConfirmOrderModalOpen(false);
        setSelectedOrder(null);
    };

    const handleOrderConfirmed = () => {
        getSheetOrders();
        setConfirmOrderModalOpen(false);
        setSelectedOrder(null);
    };

    const getSheetOrders = useCallback(async (page = 1, limit = 10) => {
        try {
            setLoading(true);
            const user = getLocalStorage("auth")?.user;
            const userRole = user?.role;
            // Prepare query parameters
            const params = {
                page: queryParams.page || page,
                limit: queryParams.size || limit,
                role: userRole || '',
                name: queryParams.name,
                phone: queryParams.phone,
                productName: queryParams.productName,
                status: queryParams.status
            };
            if (queryParams.startDate) {
                params.startDate = moment(queryParams.startDate).format('YYYY-MM-DD');
            }
            if (queryParams.endDate) {
                params.endDate = moment(queryParams.endDate).format('YYYY-MM-DD');
            }
            const response = await getData(`/sheet-orders`, params);

            // Handle paginated response from backend
            const { data, total } = response.data;

            const mappedData = data.map((item) => ({
                id: item.id,
                name: item.name,
                phone: item.phone,
                address: item.address,
                city: item.city,
                price: item.price,
                orderDate: item.orderDate,
                unit: item.unit,
                productName: item.product?.name || 'N/A',
                product: item.product,
                createdAt: item.createdAt,
                isTreated: item.isTreated || false,
                user: item.user,
                confirmationDate: item.confirmationDate,
                confirmationEnd: item.confirmationEnd,
                confirmationStatus: item.confirmationStatus,
                isDuplicate: item.isDuplicate || 0,
                assignments: item.assignments || [],
            }));

            setSheetOrders(mappedData);
            setTotalSheetOrders(total);
        } catch (error) {
            console.error("Error fetching sheet orders:", error);
            openNotification("Erreur lors du chargement des commandes", "error");
        } finally {
            setLoading(false);
        }
    }, [queryParams.endDate, queryParams.name, queryParams.page, queryParams.phone, queryParams.productName, queryParams.size, queryParams.startDate, queryParams.status]);

    useEffect(() => {
        getSheetOrders();
    }, [getSheetOrders, searchParams]);

    // Auto-refresh for specific roles when sheetOrders is empty
    useEffect(() => {
        const shouldAutoRefresh =
            (user?.role === rolesConst.Management || user?.role === rolesConst.ConfirmationConvertion) &&
            sheetOrders.length === 0;

        if (shouldAutoRefresh) {
            const interval = setInterval(() => {
                getSheetOrders();
            }, 10000); // 10 seconds

            return () => clearInterval(interval);
        }
    }, [user?.role, sheetOrders.length, getSheetOrders]);

    const handlePaginationChange = (page, size) => {
        updateQueryParams({ page, size });
    };

    return (
        <>
            <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
                <Col span={24}>
                    <h2>
                        {isSuperAdmin ? "Toutes les Commandes de Feuilles" : "Ma Commande de Feuille"}
                    </h2>
                </Col>
            </Row>

            {/* Show filters only for SuperAdmin */}
            {isSuperAdmin && (
                <SheetOrderFilter />
            )}

            <div style={{ marginBottom: "20px" }}>
                {loading ? (
                    <></>
                ) : sheetOrders.length === 0 ? (
                    <Empty
                        description="Aucune commande trouvée"
                        style={{ margin: '50px 0' }}
                    />
                ) : (
                    <Row gutter={[16, 16]}>
                        {sheetOrders.map((order) => (
                            <Col
                                key={order.id}
                                xs={24}
                                sm={24}
                                md={12}
                                lg={8}
                                xl={8}
                            >
                                <SheetOrderCard
                                    order={order}
                                    onView={handleViewOrder}
                                    onDelete={showDeleteConfirm}
                                    onMarkTreated={handleMarkTreated}
                                    onConfirmOrder={handleConfirmOrder}
                                    showActions={true}
                                    showDeleteAction={isSuperAdmin}
                                    showMarkTreatedAction={false}
                                    showConfirmOrderAction={isConfirmationConvertion}
                                    showOrderDate={isSuperAdmin}
                                    userRole={user?.role}
                                />
                            </Col>
                        ))}
                    </Row>
                )}
            </div>

            {/* Pagination for SuperAdmin */}
            {isSuperAdmin && totalSheetOrders > 0 && (
                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <Pagination
                        current={queryParams.page || 1}
                        pageSize={queryParams.size || 10}
                        total={totalSheetOrders}
                        showSizeChanger
                        pageSizeOptions={['10', '20', '50', '100']}
                        showQuickJumper
                        showTotal={(total, range) =>
                            `${range[0]}-${range[1]} de ${total} éléments`
                        }
                        onChange={(page, size) => {
                            handlePaginationChange(page, size);
                        }}
                    />
                </div>
            )}

            {/* Confirm Order Form Modal */}
            {confirmOrderModalOpen && <ConfirmOrderForm
                order={selectedOrder}
                isModalOpen={confirmOrderModalOpen}
                handleCancel={handleConfirmOrderModalCancel}
                onOrderConfirmed={handleOrderConfirmed}
            />}
        </>
    );
};

export default withRoleProtection(SheetOrders, [rolesConst.SuperAdmin, rolesConst.Management, rolesConst.ConfirmationConvertion]);
